/* eslint-disable camelcase */
import { Component } from 'react';
import PropTypes from 'prop-types';
import h from 'react-hyperscript';
import { ToastContainer } from 'react-toastify';
import Intercom from 'react-intercom';
import { Switch, Route, Redirect } from 'react-router-dom';
import pathToRegexp from 'path-to-regexp';
import { Preview } from 'react-dnd-multi-backend';
import { Label } from 'reactstrap';
import cn from 'classnames';

import 'react-toastify/dist/ReactToastify.min.css';

import { init } from '../../lib/tabManager';
import AuthService from '../../lib/AuthService';
import { knownRoutes as routes, routeMap } from '../../routes';
import dndTypes from '../../enums/dndTypes';

import HeaderContainer from '../../containers/HeaderContainer';
import BreadcrumbContainer from '../../containers/BreadcrumbContainer';
import Footer from '../../components/Footer/Footer';
import OverlayTrialHasEnded from '../../components/Overlay/OverlayTrialHasEnded';
import OverlaySubscriptionEnded from '../../components/Overlay/OverlaySubscriptionEnded';

import NotFound from '../../pages/NotFound/NotFound';

import TermsOfServiceRedirectContainer from '../../containers/TermsOfService/TermsOfServiceRedirectContainer';
import SidebarContainer from '../../containers/Sidebar/SidebarContainer';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import NpoIcon from '../../components/Npos/NpoIcon';
import VenueLocationIcon from '../../components/VenueLocations/VenueLocationIcon';

const hasSubscriptionEnded = false;
const hasTrialEnded = false;

class DefaultLayout extends Component {
  static networkHandler(dispathFn) {
    dispathFn();
  }

  static generateDnDPreview(type, item, style) {
    switch (type) {
      case dndTypes.NPO:
        return h(
          Label,
          {
            for: `select-${item.data.id}`,
            className: 'd-flex justify-content-between align-items-center w-100 m-2 cursor-pointer',
            onClick: evt => evt.stopPropagation(),
          },
          [h(NpoIcon, { type: item.data.type }), h('.d-flex.link-name', { style: { flexGrow: 1 } }, item.data.name)],
        );
      case dndTypes.LOCATION:
        return h(
          Label,
          {
            for: `select-${item.data.id}`,
            className: cn({
              'd-flex': true,
              'justify-content-between': true,
              'align-items-center': true,
              'w-100': true,
              'm-2': true,
            }),
          },
          [
            h(VenueLocationIcon, { isPortable: item.data.is_portable }),
            h('.d-flex.link-name', { style: { flexGrow: 1 } }, `${item.data.identifier} / ${item.data.name}`),
          ],
        );
      default:
        return h('span', { style }, 'no preview');
    }
  }

  componentWillMount() {
    const { loginSuccess, networkOnline, networkOffline } = this.props; // eslint-disable-line
    const { isAuthenticated, getProfile } = AuthService;

    if (isAuthenticated()) {
      loginSuccess(getProfile());
    }

    const { Offline } = window;

    if (Offline) {
      Offline.on('confirmed-down', () => DefaultLayout.networkHandler(networkOffline));
      Offline.on('confirmed-up', () => DefaultLayout.networkHandler(networkOnline));
    }

    init();
  }

  componentWillUnmount() {
    const { networkOnline, networkOffline } = this.props; // eslint-disable-line
    const { Offline } = window;

    if (Offline) {
      Offline.off('confirmed-down', () => DefaultLayout.networkHandler(networkOffline));
      Offline.off('confirmed-up', () => DefaultLayout.networkHandler(networkOnline));
    }
  }

  render() {
    const { data } = this.props;
    const { loading, me } = data || {};

    const isRedirectRoute = r => !!r.redirect;
    const isNotRedirectRoute = r => !isRedirectRoute(r);
    const isRedirectWithParamsRoute = r => !!r.redirectWithParams;
    const hasApprovedTOS = !loading && me && me.terms_of_service_approval;

    const { REACT_APP_INTERCOM_APP_ID } = process.env;

    const {
      location,
      match,
      network: { isOnline },
      profile = {},
      profile: { id: user_id, email, intercomHash: user_hash, first, last, authorization } = {},
    } = this.props;

    const intercomUser = REACT_APP_INTERCOM_APP_ID &&
      profile && {
        email,
        name: `${first} ${last}`,
        user_id,
        user_hash,
        authorization,
      };

    const convertToRoute = route => {
      const { name, path, component: tmpComponent = NotFound, exact = true, requiresAccessToken = true } = route;
      let component = tmpComponent;

      if (requiresAccessToken) {
        if (hasSubscriptionEnded) {
          component = OverlaySubscriptionEnded;
        } else if (hasTrialEnded) {
          component = OverlayTrialHasEnded;
        } else if (!hasApprovedTOS && name !== routeMap.TERMS_OF_SERVICE_APPROVAL.name) {
          component = TermsOfServiceRedirectContainer;
        }
      }

      return h(Route, { path, name, exact, component });
    };

    const renderedNormalRoutes = routes.filter(isNotRedirectRoute).map(convertToRoute);

    const renderNotFoundRoutes = [
      // ... any fallback routes should be added Here

      // Must be last to ensure it is final route to handle unknowns
      h(Redirect, { to: '/404' }),
    ];

    const renderedRedirectRoutes = routes
      .filter(isRedirectRoute)
      .map(({ path: from, redirect: to, exact = true } = {}) => h(Redirect, { exact, from, to }));

    const renderedRedirectWithParamsRoutes = routes
      .filter(isRedirectWithParamsRoute)
      .map(({ path, redirectWithParams: to, exact = true } = {}) => {
        const pathTo = pathToRegexp.compile(to);

        const component = ({ match: { params } = {} }) => h(Redirect, { to: pathTo(params) });

        return h(Route, { exact, path, component });
      });

    const toastDefaultAutoClose = document.body.getElementsByTagName('test').length ? 500 : 5 * 1000; // Used for quickly closing toast items during testing

    return h(Route, {
      render: () =>
        h(ScrollToTop, [
          h('.app', [
            h(HeaderContainer),
            h('.body-slideout-overlay', {
              onClick: () => {
                document.body.classList.remove('sidebar-mobile-show');
              },
            }),
            h('.app-body', [
              h(SidebarContainer, { profile, location, me }),
              h('main.main', [
                h(BreadcrumbContainer, { profile, location, match }),
                h('.container-fluid', [
                  h('.offline', { className: !isOnline && 'show' }, [
                    h('.on.alert.alert-success', [h('strong', 'ONLINE')]),
                    h('.off.alert.alert-danger', [h('strong', 'OFFLINE')]),
                  ]),
                  h(Switch, [
                    // All redirects to known routes
                    ...renderedRedirectRoutes,
                    // All redirects to known routes that use params
                    ...renderedRedirectWithParamsRoutes,
                    // All known routes
                    ...renderedNormalRoutes,
                    // Fallbacks to unmapped routes
                    ...renderNotFoundRoutes,
                  ]),
                ]),
              ]),
            ]),
            h(Footer),
            h(ToastContainer, { position: 'top-right', autoClose: toastDefaultAutoClose }),
            intercomUser && h(Intercom, { appID: REACT_APP_INTERCOM_APP_ID, ...intercomUser }),
            h(Preview, { generator: (type, item, style) => DefaultLayout.generateDnDPreview(type, item, style) }),
          ]),
        ]),
    });
  }
}

DefaultLayout.defaultProps = {
  location: '/',
  match: null,
  network: {
    isOnline: true,
  },
  profile: {
    id: null,
    email: null,
    intercomHash: null,
    first: null,
    last: null,
    authorization: {},
  },
};

const { string, bool, shape, arrayOf } = PropTypes;

DefaultLayout.propTypes = {
  location: shape({
    pathname: string,
  }),
  match: shape({}),
  network: shape({
    isOnline: bool.isRequired,
  }),
  profile: shape({
    id: string,
    email: string,
    intercomHash: string,
    first: string,
    last: string,
    authorization: shape({
      groups: arrayOf(string.isRequired),
      roles: arrayOf(string.isRequired),
    }),
  }),
  data: shape({}).isRequired,
};

export default DefaultLayout;



// WEBPACK FOOTER //
// ./src/layouts/Default/index.js